import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { formatMoney } from '../../util/currency';
import { timestampToDate, daysBetween, addTime } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
//Damage Deposits
import SectionDamageDepositMaybe from './SectionDamageDepositMaybe';

import { Page, LayoutSingleColumn } from '../../components';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './ListingPage.module.css';
import { findApplicableDiscount, getDayBasedPricing, getDateBasedPricing } from '../../util/data';

/**
 * This file contains shared functions from each ListingPage variants.
 */

const { UUID } = sdkTypes;

/**
 * Helper to get formattedPrice and priceTitle for SectionHeading component.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'USD')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceData = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Get category's label.
 *
 * @param {Array} categories array of category objects (key & label)
 * @param {String} value selected category value
 * @returns label for the selected value
 */
export const categoryLabel = (categories, value) => {
  const cat = categories.find(c => c.key === value);
  return cat ? cat.label : value;
};

/**
 * Filter listing images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} listing
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const listingImages = (listing, variantName) =>
  (listing.images || [])
    .map(image => {
      const variants = image.attributes.variants;
      const variant = variants ? variants[variantName] : null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      return variant || size;
    })
    .filter(variant => variant != null);

/**
 * Callback for the "contact" button on ListingPage to open inquiry modal.
 *
 * @param {Object} parameters all the info needed to open inquiry modal.
 */
export const handleContactUser = parameters => () => {
  const {
    history,
    params,
    currentUser,
    callSetInitialValues,
    location,
    routes,
    setInitialValues,
    setInquiryModalOpen,
  } = parameters;

  if (!currentUser) {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };

    // We need to log in before showing the modal, but first we need to ensure
    // that modal does open when user is redirected back to this listingpage
    callSetInitialValues(setInitialValues, { inquiryModalOpenForListingId: params.id });

    // signup and return back to listingPage.
    history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
  } else {
    setInquiryModalOpen(true);
  }
};

/**
 * Callback for the inquiry modal to submit aka create inquiry transaction on ListingPage.
 * Note: this is for booking and purchase processes. Inquiry process is handled through handleSubmit.
 *
 * @param {Object} parameters all the info needed to create inquiry.
 */
export const handleSubmitInquiry = parameters => values => {
  const { history, params, getListing, onSendInquiry, routes, setInquiryModalOpen } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const { message } = values;

  onSendInquiry(listing, message.trim())
    .then(txId => {
      setInquiryModalOpen(false);

      // Redirect to OrderDetailsPage
      history.push(createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {}));
    })
    .catch(() => {
      // Ignore, error handling in duck file
    });
};

/**
 * Handle order submit from OrderPanel.
 *
 * @param {Object} parameters all the info needed to redirect user to CheckoutPage.
 */
export const handleSubmit = parameters => values => {
  const {
    history,
    params,
    currentUser,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    routes,
    price,
    config
  } = parameters;

  const listingId = new UUID(params.id);
  const listing = getListing(listingId);

  const damageDeposit = listing.attributes.publicData?.damageDeposit;
  const delivery = listing.attributes.publicData?.delivery;

  const {
    bookingDates,
    bookingStartTime,
    bookingEndTime,
    bookingStartDate, // not relevant (omit)
    bookingEndDate, // not relevant (omit)
    quantity: quantityRaw,
    addons: formAddons,
    addonsQuantity,
    deliveryMethod,
    seats,
    discountCode,
    ...otherOrderData
  } = values;

  const addonsFromPublicData = listing.attributes.publicData.addons;
  const addons = formAddons?.map(addonKey => {
    const addonConfig = addonsFromPublicData.find(a => a.key === addonKey);
    const addonQuantity = addonsQuantity ? addonsQuantity[addonKey] : 1;
    return {
      ...addonConfig,
      quantity: addonQuantity,
    };
  });
  const addonsMaybe = addons ? { addons } : {};

  // Find the discount
  const discountsFromPublicData = listing.attributes.publicData.discounts;

  const hasBookingDates = bookingDates?.startDate && bookingDates?.endDate;

  // The picker stores the end date as the LAST rental day (inclusive).
  // Convert to Sharetribe's exclusive-end convention before doing any
  // downstream math so day counts, pricing, and the order data we hand
  // off to the server all agree.
  const exclusiveEndDate = hasBookingDates
    ? addTime(bookingDates.endDate, 1, 'days')
    : null;
  const adjustedBookingDates = hasBookingDates
    ? { startDate: bookingDates.startDate, endDate: exclusiveEndDate }
    : bookingDates;

  const datesDayDifference = hasBookingDates ? daysBetween(bookingDates.startDate, exclusiveEndDate) : undefined;

  const durationDiscount = findApplicableDiscount(discountsFromPublicData, datesDayDifference);

  const durationDiscountMaybe = durationDiscount
    ? {
        durationDiscount,
      }
    : {};
    
  const dayPricings = listing.attributes.publicData.dayPricings;
  const datePricings = listing.attributes.publicData.datePricings;

  const shouldFetchDayBasedPricing = hasBookingDates && dayPricings;
  const shouldFetchDateBasedPricing = hasBookingDates && datePricings;

  const dayBasedPricing = shouldFetchDayBasedPricing && getDayBasedPricing(adjustedBookingDates, dayPricings, price, config);
  const dateBasedPricing = shouldFetchDateBasedPricing && getDateBasedPricing(adjustedBookingDates, datePricings, price, config);

  const dayBasedPricingMaybe = dayBasedPricing ? { dayBasedPricing } : {};
  const dateBasedPricingMaybe = dateBasedPricing ? { dateBasedPricing } : {};

  const bookingMaybe = bookingDates
    ? {
        bookingDates: {
          bookingStart: bookingDates.startDate,
          bookingEnd: exclusiveEndDate,
        },
      }
    : bookingStartTime && bookingEndTime
    ? {
        bookingDates: {
          bookingStart: timestampToDate(bookingStartTime),
          bookingEnd: timestampToDate(bookingEndTime),
        },
      }
    : {};
  const quantity = Number.parseInt(quantityRaw, 10);
  const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

  const seatsMaybe = seats ? { seats: Number(seats) } : {};

  const initialValues = {
    listing,
    orderData: {
      damageDeposit,
      delivery,
      ...bookingMaybe,
      ...quantityMaybe,
      ...addonsMaybe,
      ...durationDiscountMaybe,
      ...dayBasedPricingMaybe,
      ...dateBasedPricingMaybe,
      ...deliveryMethodMaybe,
      ...seatsMaybe,
      ...otherOrderData,
      discountCode,
    },
    confirmPaymentError: null,
  };

  const saveToSessionStorage = !currentUser;

  // Customize checkout page state with current listing and selected orderData
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

  callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

  // Clear previous Stripe errors from store if there is any
  onInitializeCardPaymentData();

  // Redirect to CheckoutPage
  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

/**
 * Create fallback views for the ListingPage: LoadingPage and ErrorPage.
 * The PlainPage is just a helper for them.
 */
const PlainPage = props => {
  const { title, topbar, scrollingDisabled, children } = props;
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        {children}
      </LayoutSingleColumn>
    </Page>
  );
};

export const ErrorPage = props => {
  const { topbar, scrollingDisabled, invalidListing, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.errorText}>
      {invalidListing ? (
          <FormattedMessage id="ListingPage.errorInvalidListingMessage" />
        ) : (
          <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
        )}      </p>
    </PlainPage>
  );
};

export const LoadingPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.loadingText}>
        <FormattedMessage id="ListingPage.loadingListingMessage" />
      </p>
    </PlainPage>
  );
};

/**
 * Converts Money object to number, which is needed for the search schema (for Google etc.)
 *
 * @param {Money} price
 * @returns {Money|null}
 */
export const priceForSchemaMaybe = price => {
  try {
    const schemaPrice = convertMoneyToNumber(price);
    return {
      price: schemaPrice.toFixed(2),
      priceCurrency: price.currency,
    };
  } catch (e) {
    return {};
  }
};
